/**
 * The taxonomies, frameworks and alignments the app knows, and the guidance
 * strategy for each framework and taxonomy pair.
 *
 * @module taxonomy/registry
 */

import { BLOOMS_TAXONOMY } from "./blooms";
import { EQF_BLOOMS_ALIGNMENT, EQF_FRAMEWORK } from "./eqf-alignment";
import { NFQ_BLOOMS_ALIGNMENT, NFQ_FRAMEWORK } from "./nfq-alignment";
import { type GuidanceStrategy, verbListStrategy } from "./strategy";
import type { Alignment, QualificationFramework, Taxonomy } from "./types";

const TAXONOMIES: readonly Taxonomy[] = [BLOOMS_TAXONOMY];
const FRAMEWORKS: readonly QualificationFramework[] = [NFQ_FRAMEWORK, EQF_FRAMEWORK];
const ALIGNMENTS: readonly Alignment[] = [NFQ_BLOOMS_ALIGNMENT, EQF_BLOOMS_ALIGNMENT];

export function getTaxonomy(id: string): Taxonomy | undefined {
  return TAXONOMIES.find((t) => t.id === id);
}

export function getFramework(id: string): QualificationFramework | undefined {
  return FRAMEWORKS.find((f) => f.id === id);
}

export function getAlignment(frameworkId: string, taxonomyId: string): Alignment | undefined {
  return ALIGNMENTS.find((a) => a.frameworkId === frameworkId && a.taxonomyId === taxonomyId);
}

const strategies = new Map<string, GuidanceStrategy>();

/** The strategy for a framework and taxonomy pair; undefined when they are not aligned. */
export function getGuidanceStrategy(
  frameworkId: string,
  taxonomyId: string,
): GuidanceStrategy | undefined {
  const key = `${frameworkId}/${taxonomyId}`;
  const cached = strategies.get(key);
  if (cached) {
    return cached;
  }
  const taxonomy = getTaxonomy(taxonomyId);
  const framework = getFramework(frameworkId);
  const alignment = getAlignment(frameworkId, taxonomyId);
  if (!taxonomy || !framework || !alignment) {
    return undefined;
  }
  const strategy = verbListStrategy(taxonomy, framework, alignment);
  strategies.set(key, strategy);
  return strategy;
}

/** Bloom's against the Irish NFQ, which every programme uses today. */
export const DEFAULT_GUIDANCE: GuidanceStrategy = verbListStrategy(
  BLOOMS_TAXONOMY,
  NFQ_FRAMEWORK,
  NFQ_BLOOMS_ALIGNMENT,
);
